import {
	ArrowLeft,
	BadgeCheck,
	Building2,
	FileText,
	Save,
	User,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useOrganization } from '@/api/queries/organization-queries'
import { PushNotificationsCard } from '@/components/push-notifications-card'
import { Button } from '@/components/ui/button'
import { CardFooter } from '@/components/ui/card'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { ROLE_LABELS } from '@/constants/role-labels'
import { ROUTES } from '@/constants/routes'
import { useAuthStore, useProfileStore } from '@/store'

const appVersion = import.meta.env.VITE_VERSION || 'dev'

export const ProfilePage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()

	const user = useAuthStore((s) => s.user)
	const isAdmin = user?.role === 'ADMIN'
	const userName = user?.name ?? 'Usuário'
	const userBadge = user?.badge ?? ''
	const userRole = user?.role ?? 'USER'
	const userCr = user?.crName ?? ''

	const {
		defaultBranchId,
		defaultContractId,
		defaultCustomerId,
		setDefaultBranch,
		setDefaultContract,
		setDefaultCustomer,
	} = useProfileStore()

	const [selectedBranch, setSelectedBranch] = useState(defaultBranchId)
	const [selectedContract, setSelectedContract] = useState(defaultContractId)
	const [selectedCustomer, setSelectedCustomer] = useState(defaultCustomerId)

	const orgQuery = useOrganization()
	const branchOptions = orgQuery.data ?? []

	const contractOptions =
		branchOptions
			.find((b) => b.id === selectedBranch)
			?.crs.map((cr) => ({ id: cr.id, name: cr.name })) ?? []

	const customerOptions =
		branchOptions
			.find((b) => b.id === selectedBranch)
			?.crs.find((cr) => cr.id === selectedContract)?.customers ?? []
	const hasCustomers = customerOptions.length > 0

	const handleSave = () => {
		if (!selectedBranch) {
			toast.error(t('profile.emptyBranchError'))
			return
		}
		if (!selectedContract) {
			toast.error(t('profile.emptyContractError'))
			return
		}
		const branchName =
			branchOptions.find((b) => b.id === selectedBranch)?.name ?? ''
		const contractName =
			contractOptions.find((c) => c.id === selectedContract)?.name ?? ''
		const customerName = selectedCustomer
			? (customerOptions.find((cust) => cust.id === selectedCustomer)?.name ??
				'')
			: ''

		if (hasCustomers) {
			if (!selectedCustomer) {
				toast.error(t('profile.emptyCustomerError'))
				return
			}

			setDefaultCustomer(selectedCustomer, customerName)
		} else {
			setDefaultCustomer('', '')
		}
		setDefaultBranch(selectedBranch, branchName)
		setDefaultContract(selectedContract, contractName)
		toast.success(t('profile.saveSuccess'))
		navigate(ROUTES.DASHBOARD.HOME)
	}

	return (
		<div className="flex flex-1 flex-col">
			<div className="flex-1 bg-white sm:bg-kaefer-light-silver/50">
				{/* ── Desktop/tablet header — hidden on mobile ── */}
				<div className="hidden bg-kaefer-dark-grey px-6 py-6 sm:block">
					<div className="mx-auto flex max-w-3xl items-center gap-3">
						<Button
							variant="ghost"
							size="icon-sm"
							onClick={() => navigate(ROUTES.DASHBOARD.HOME)}
							className="shrink-0 text-white/70 hover:bg-white/10 hover:text-white"
						>
							<ArrowLeft className="size-5" />
						</Button>
						<div>
							<h1 className="text-xl font-semibold text-white">
								{t('profile.title')}
							</h1>
							<p className="mt-0.5 text-sm text-white/60">
								{t('profile.subtitle')}
							</p>
						</div>
					</div>
				</div>

				{/* ── Mobile back — hidden on sm+ ── */}
				<div className="px-4 pt-4 sm:hidden">
					<Button
						variant="ghost"
						size="icon-sm"
						onClick={() => navigate(ROUTES.DASHBOARD.HOME)}
						className="-ml-1 text-kaefer-medium-grey hover:text-kaefer-dark-grey"
					>
						<ArrowLeft className="size-5" />
					</Button>
				</div>

				{/* ── Content ── */}
				<div className="mx-auto max-w-3xl px-4 py-4 sm:px-6 sm:py-6">
					<PushNotificationsCard variant="section" />
					<div className="sm:rounded-xl sm:border sm:border-kaefer-light-silver sm:bg-white sm:shadow-sm">
						{/* Avatar + name block */}
						<div className="flex items-center gap-4 px-0 py-4 sm:px-6 sm:py-5">
							<div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-kaefer-dark-grey">
								<User className="size-6 text-white" />
							</div>
							<div className="min-w-0 flex-1">
								<p className="truncate text-base font-semibold text-kaefer-dark-grey">
									{userName}
								</p>
								<div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-kaefer-medium-grey">
									<span className="flex items-center gap-1">
										<BadgeCheck className="size-3.5" />
										{userBadge}
									</span>
									<span className="inline-flex items-center rounded-full bg-kaefer-medium-grey/10 px-2 py-0.5 text-xs font-medium text-kaefer-medium-grey">
										{ROLE_LABELS[userRole] ?? userRole}
									</span>
								</div>
								{userCr && (
									<p className="mt-1 flex items-center gap-1 truncate text-xs text-kaefer-silver">
										<FileText className="size-3" />
										{userCr}
									</p>
								)}
							</div>
						</div>

						{/* Admin section — change defaults */}
						{isAdmin && (
							<>
								<div className="border-t border-kaefer-light-silver" />

								<div className="py-4 sm:px-6 sm:py-5">
									<div className="mb-4 flex items-center gap-2">
										<Building2 className="size-4 text-kaefer-medium-grey" />
										<span className="text-sm font-semibold text-kaefer-dark-grey">
											{t('profile.changeDefaultsTitle')}
										</span>
									</div>

									<div className="grid gap-4 sm:grid-cols-2">
										{/* Branch */}
										<div className="space-y-1.5">
											<label
												htmlFor="defaultBranch"
												className="text-xs font-medium text-kaefer-medium-grey"
											>
												{t('profile.newBranchLabel')}
											</label>
											<NativeSelect
												id="defaultBranch"
												value={selectedBranch}
												onChange={(e) => {
													setSelectedBranch(e.target.value)
													setSelectedContract('')
													setSelectedCustomer('')
												}}
												className="h-10"
											>
												<NativeSelectOption value="" disabled>
													{t('profile.newBranchPlaceholder')}
												</NativeSelectOption>
												{branchOptions.map((b) => (
													<NativeSelectOption key={b.id} value={b.id}>
														{b.name}
													</NativeSelectOption>
												))}
											</NativeSelect>
										</div>

										{/* Contract */}
										<div className="space-y-1.5">
											<label
												htmlFor="defaultContract"
												className="text-xs font-medium text-kaefer-medium-grey"
											>
												{t('profile.newContractLabel')}
											</label>
											<NativeSelect
												id="defaultContract"
												value={selectedContract}
												onChange={(e) => {
													setSelectedContract(e.target.value)
													setSelectedCustomer('')
												}}
												disabled={!selectedBranch}
												className="h-10"
											>
												<NativeSelectOption value="" disabled>
													{t('profile.newContractPlaceholder')}
												</NativeSelectOption>
												{contractOptions.map((c) => (
													<NativeSelectOption key={c.id} value={c.id}>
														{c.name}
													</NativeSelectOption>
												))}
											</NativeSelect>
										</div>

										{/* Customer — only show if CR has customers */}
										{hasCustomers && (
											<div className="space-y-1.5">
												<label
													htmlFor="defaultCustomer"
													className="text-xs font-medium text-kaefer-medium-grey"
												>
													{t('inspections.common.customer')}
												</label>
												<NativeSelect
													id="defaultCustomer"
													value={selectedCustomer}
													onChange={(e) => setSelectedCustomer(e.target.value)}
													disabled={!selectedContract || !hasCustomers}
													className="h-10"
												>
													<NativeSelectOption value="">
														{t('inspections.common.selectCustomer')}
													</NativeSelectOption>
													{customerOptions.map((cust) => (
														<NativeSelectOption key={cust.id} value={cust.id}>
															{cust.name}
														</NativeSelectOption>
													))}
												</NativeSelect>
											</div>
										)}
									</div>

									{/* Save button */}
									<div className="mt-5">
										<Button
											onClick={handleSave}
											variant="kaefer-grey"
											className="w-full gap-2 sm:w-auto"
										>
											<Save className="size-4" />
											{t('profile.saveButton')}
										</Button>
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
			<CardFooter className="flex items-center justify-center bg-kaefer-dark-grey py-4 text-white/70">
				<p className="w-full text-center text-xs text-muted-foreground">
					SSMA {appVersion}
				</p>
			</CardFooter>
		</div>
	)
}
